import React from 'react';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { useCoachUsage } from '../context/CoachUsageContext';
import SwapArrowsIcon from './icons/SwapArrowsIcon';
import { useLocale } from '../context/LocaleContext';

// - [x] Refactor `PlayerStatsNew.js` dashboard component from a swipeable carousel to a unified "Bento Box" grid layout.
// - [/] Update `QuestCard.js` to use full-width verification/coach buttons on mobile for better ergonomics.
const QuestCard = ({
  quest,
  handleVerifyClick,
  onViewDetails,
  handleCoachClick,
  handleMoveSwapClick,
  canMoveSwap = false,
  isToday = true,
  activeAurasEffects = [],
  layout = 'horizontal'
}) => {
  const { user } = useAuth(); // Get user from AuthContext
  const { t } = useLocale();
  const Icon = quest.icon;
  const now = Date.now();
  const isHealthKitQuest = quest?.detailedDescription?.verificationType === 'healthkit';
  const isMindQuest = quest?.type === 'Mind';
  const isLockedTeaser = Boolean(quest?.isLockedTeaser);
  const healthKitProgressLabel = quest?.detailedDescription?.healthkit?.progressLabel || null;
  const preWorkoutActive = Array.isArray(activeAurasEffects)
    ? activeAurasEffects.some((eff) => eff?.type === 'preworkout' && eff.expiresAt && new Date(eff.expiresAt).getTime() > now)
    : false;
  const displayedXp = preWorkoutActive ? quest.xp * 2 : quest.xp;
  const xpBadgeLabel = isLockedTeaser ? t('questCard.healthKitOnly', 'HealthKit only') : `+${displayedXp} XP`;

  const handleCardClick = (e) => {
    // Prevent card click if a button was clicked
    if (e.target.closest('button')) {
      return;
    }
    if (onViewDetails) {
      onViewDetails(quest);
    }
  };

  const { coachLimitReached } = useCoachUsage();

  const canShowCoachButton =
    isToday &&
    !quest.completed &&
    !isHealthKitQuest &&
    user?.subscription?.status === 'ultra' &&
    (quest.type === 'Workout' || quest.type === 'Mindfulness');

  const moveSwapButton = canMoveSwap ? (
    <button
      type="button"
      aria-label={t('questCard.moveSwap', 'Move or swap quest')}
      title={t('questCard.moveSwap', 'Move or swap quest')}
      onClick={(e) => {
        e.stopPropagation();
        handleMoveSwapClick?.(quest);
      }}
      className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-400/20 bg-slate-950/70 text-cyan-300 transition-colors hover:border-cyan-300/40 hover:bg-slate-900 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
    >
      <SwapArrowsIcon className="h-4 w-4" />
    </button>
  ) : null;



  // --- VERTICAL LAYOUT (For Dashboard Carousel) ---
  if (layout === 'vertical') {
    return (
      <div
        className="group relative bg-slate-900/40 rounded-[1.5rem] p-0.5 shadow-xl hover:shadow-cyan-500/10 border border-white/10 backdrop-blur-md transition-all duration-300 cursor-pointer overflow-hidden h-full flex flex-col"
        onClick={handleCardClick}
      >
        {/* Hover Border Effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

        <div className="relative bg-slate-950/50 rounded-[1.3rem] p-5 h-full flex flex-col items-center text-center gap-4 group-hover:bg-slate-900/50 transition-colors duration-300">

          {/* XP Badge (Top Right) */}
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
            {preWorkoutActive && !isLockedTeaser && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-cyan-600/40 to-blue-600/40 text-cyan-200 border border-cyan-500/30 shadow-sm backdrop-blur-sm">
                2x
              </span>
            )}
            <div className="px-2.5 py-1 rounded-lg bg-slate-950/60 border border-white/5 backdrop-blur-md">
              <span className={`text-xs font-bold drop-shadow-sm ${isLockedTeaser ? 'text-cyan-200' : 'text-yellow-400'}`}>
                {xpBadgeLabel}
              </span>
            </div>
            {moveSwapButton}
          </div>

          {/* Icon Section (Centered) */}
          <div className="relative mt-2">
            <div className="absolute inset-0 bg-cyan-400/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative bg-slate-900/80 p-4 rounded-2xl border border-white/5 group-hover:border-cyan-500/30 transition-colors duration-300 shadow-inner">
              <Icon className="w-8 h-8 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-300" />
            </div>
          </div>

          {/* Content Section (Centered) */}
          <div className="flex-grow flex flex-col justify-center w-full px-1">
            <h4 className="font-bold text-white text-xl leading-tight line-clamp-2 group-hover:text-cyan-50 transition-colors duration-300">
              {quest.name}
            </h4>
            <p className="text-sm text-gray-400 line-clamp-2 mt-2 group-hover:text-gray-300 transition-colors duration-300">
              {quest.description}
            </p>
            {healthKitProgressLabel && (
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-cyan-300">
                {healthKitProgressLabel}
              </p>
            )}
          </div>

          {/* Actions Section (Bottom) */}
          <div className="w-full mt-auto pt-2">
            {isToday && (
              quest.completed ? (
                <button disabled className='w-full flex justify-center items-center gap-2 bg-green-500/10 border border-green-500/50 text-green-400 font-bold py-2.5 px-4 rounded-xl text-sm shadow-sm cursor-default backdrop-blur-sm'>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('questCard.completed', 'Completed')}
                </button>
              ) : isLockedTeaser ? (
                <button
                  disabled
                  className="w-full flex justify-center items-center gap-2 bg-slate-900/70 border border-amber-300/25 text-amber-200 font-bold py-2.5 px-4 rounded-xl text-sm shadow-sm cursor-default backdrop-blur-sm"
                >
                  {t('questCard.locked', 'Locked')}
                </button>
              ) : isHealthKitQuest ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onViewDetails?.(quest); }}
                  className="
                    w-full
                    relative overflow-hidden group/btn flex items-center justify-center
                    bg-gradient-to-r from-sky-500 to-cyan-600 text-white font-bold py-2.5 rounded-xl text-sm
                    shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                    hover:from-sky-400 hover:to-cyan-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                  "
                >
                  <span className="relative z-10">{t('questCard.viewHealthStatus', 'View health status')}</span>
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-3 w-full">
                  {canShowCoachButton ? (
                    <>
                      <button
                        disabled={coachLimitReached}
                        onClick={(e) => { e.stopPropagation(); handleCoachClick(quest); }}
                        className={`
                          relative overflow-hidden group/btn flex items-center justify-center
                          ${coachLimitReached
                            ? 'bg-slate-700 opacity-50 cursor-not-allowed text-gray-400'
                            : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-900/30'
                          }
                          transition-all duration-200 py-2.5 rounded-xl text-xs font-bold tracking-wide
                        `}
                      >
                        <span className="relative z-10 flex items-center gap-1">
                          {t('questCard.live', 'Live')}
                        </span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleVerifyClick(quest); }}
                        className="
                          relative overflow-hidden group/btn flex items-center justify-center
                          bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-2.5 rounded-xl text-xs
                          shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                          hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                        "
                      >
                        <span className="relative z-10">{isMindQuest ? t('questCard.start', 'Start') : t('questCard.verify', 'Verify')}</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleVerifyClick(quest); }}
                      className="
                          col-span-2
                          relative overflow-hidden group/btn flex items-center justify-center
                          bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-2.5 rounded-xl text-sm
                          shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                          hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                        "
                    >
                      <span className="relative z-10">{isMindQuest ? t('questCard.start', 'Start') : t('questCard.verify', 'Verify')}</span>
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- HORIZONTAL LAYOUT (For Calendar / List View) ---
  return (
    <div
      className="group relative bg-slate-900/40 rounded-[1.5rem] p-0.5 shadow-xl hover:shadow-cyan-500/10 border border-white/10 backdrop-blur-md transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={handleCardClick}
    >
      {/* Hover Border Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="relative bg-slate-950/50 rounded-[1.3rem] p-4 h-full flex flex-col gap-4 group-hover:bg-slate-900/50 transition-colors duration-300">

        {/* Top Section */}
        <div className="flex items-start gap-4">
          {/* Icon Section */}
          <div className="relative flex-shrink-0 mt-0.5">
            <div className="absolute inset-0 bg-cyan-400/20 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative bg-slate-900/80 p-3.5 rounded-xl border border-white/5 group-hover:border-cyan-500/30 transition-colors duration-300 shadow-inner">
              <Icon className="w-6 h-6 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-300" />
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-grow min-w-0 flex flex-col justify-center">
            <h4 className="font-bold text-white text-lg leading-tight truncate group-hover:text-cyan-50 transition-colors duration-300">
              {quest.name}
            </h4>
            <p className="text-sm text-gray-400 line-clamp-2 mt-0.5 group-hover:text-gray-300 transition-colors duration-300">
              {quest.description}
            </p>
          </div>

          {/* XP Badge */}
          <div className="flex-shrink-0 flex flex-col items-end gap-1">
            {preWorkoutActive && !isLockedTeaser && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-cyan-600/40 to-blue-600/40 text-cyan-200 border border-cyan-500/30 shadow-sm backdrop-blur-sm">
                2x
              </span>
            )}
            <div className="flex items-center px-2 py-1 rounded-lg bg-slate-950/60 border border-white/5 shadow-inner">
              <span className={`text-xs font-bold drop-shadow-sm ${isLockedTeaser ? 'text-cyan-200' : 'text-yellow-400'}`}>
                {xpBadgeLabel}
              </span>
            </div>
            {moveSwapButton}
          </div>
        </div>
        {healthKitProgressLabel && (
          <div className="text-xs font-semibold uppercase tracking-wide text-cyan-300 -mt-2">
            {healthKitProgressLabel}
          </div>
        )}

        {/* Bottom Actions Section */}
        {isToday && (
          <div className="w-full mt-1">
            {quest.completed ? (
              <button disabled className='w-full flex justify-center items-center gap-2 bg-green-500/10 border border-green-500/50 text-green-400 font-bold py-2.5 px-4 rounded-xl text-sm shadow-sm cursor-default backdrop-blur-sm'>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                {t('questCard.completed', 'Completed')}
              </button>
            ) : isLockedTeaser ? (
              <button
                disabled
                className="w-full flex justify-center items-center gap-2 bg-slate-900/70 border border-amber-300/25 text-amber-200 font-bold py-3 px-4 rounded-xl text-sm shadow-sm cursor-default backdrop-blur-sm"
              >
                {t('questCard.locked', 'Locked')}
              </button>
            ) : isHealthKitQuest ? (
              <button
                onClick={(e) => { e.stopPropagation(); onViewDetails?.(quest); }}
                className="
                  col-span-2
                  relative overflow-hidden group/btn flex items-center justify-center
                  bg-gradient-to-r from-sky-500 to-cyan-600 text-white font-bold py-3 rounded-xl text-sm
                  shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                  w-full hover:from-sky-400 hover:to-cyan-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                "
              >
                <span className="relative z-10">{t('questCard.viewHealthStatus', 'View health status')}</span>
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3 w-full">
                {canShowCoachButton ? (
                  <>
                    <button
                      disabled={coachLimitReached}
                      onClick={(e) => { e.stopPropagation(); handleCoachClick(quest); }}
                      className={`
                        relative overflow-hidden group/btn flex items-center justify-center
                        ${coachLimitReached
                          ? 'bg-slate-700 opacity-50 cursor-not-allowed text-gray-400'
                          : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-900/30'
                        }
                        transition-all duration-200 py-3 rounded-xl text-sm font-bold tracking-wide active:scale-[0.98]
                      `}
                    >
                      <span className="relative z-10 flex items-center gap-1">{t('questCard.liveCoach', 'Live Coach')}</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleVerifyClick(quest); }}
                      className="
                          relative overflow-hidden group/btn flex items-center justify-center
                          bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 rounded-xl text-sm
                          shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                          hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                      "
                    >
                      <span className="relative z-10">{isMindQuest ? t('questCard.start', 'Start') : t('questCard.verify', 'Verify')}</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleVerifyClick(quest); }}
                    className="
                      col-span-2
                      relative overflow-hidden group/btn flex items-center justify-center
                      bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 rounded-xl text-sm
                      shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                      w-full hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                    "
                  >
                    <span className="relative z-10">{isMindQuest ? t('questCard.startMindSession', 'Start Mind session') : t('questCard.verifyQuest', 'Verify quest')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestCard;
